/**
 * Roles that unlock manager-level actions: managing the Data Entry catalogues
 * (rate types, status messages) and closing a case. An Administrator sits above
 * an Operation-Manager, so everything a manager may do an administrator may do
 * as well (OS-542).
 */
export const MANAGER_OR_ABOVE = ['Administrator', 'Operation-Manager'];

/**
 * Roles that unlock assistant-manager-level actions: approving or rejecting an
 * invoice/crdr, using the "Others - Manager only" status message, creating and
 * deleting quotes, and editing other people's guidelines and comments.
 */
export const ASSISTANT_MANAGER_OR_ABOVE = [...MANAGER_OR_ABOVE, 'Operation-Assistant Manager'];

/** Roles that may sign in and work in the system at all. */
export const ACTIVE_ROLES = [...ASSISTANT_MANAGER_OR_ABOVE, 'Operation-Staff'];

/**
 * Whether the user has been marked Inactive. Departures are handled by giving
 * the Inactive role in Auth0 while the old roles stay for history, so this
 * single role must win over everything else the user still holds.
 * @param {Array<string>} userRole roles from the Auth0 token
 * @returns {boolean}
 */
export const isInactive = (userRole) => Array.isArray(userRole) && userRole.includes('Inactive');

/**
 * Whether the user may use the system: not Inactive and holding at least one
 * working role. Anyone else is sent to the inactive page.
 * @param {Array<string>} userRole roles from the Auth0 token
 * @returns {boolean}
 */
export const hasActiveRole = (userRole) => hasAnyRole(userRole, ACTIVE_ROLES);

/**
 * Whether the signed-in user holds at least one of the allowed roles.
 * A user can carry several Auth0 roles at once (for example Administrator and
 * Operation-Manager together), so a permission is granted when any of them
 * qualifies — never by matching a single role exactly. A user who has been
 * given Inactive (how departures are handled; the old roles stay for history)
 * is denied everything regardless of the other roles.
 * @param {Array<string>} userRole roles from the Auth0 token
 * @param {Array<string>} allowed roles that unlock the action
 * @returns {boolean}
 */
export const hasAnyRole = (userRole, allowed) =>
    Array.isArray(userRole) &&
    !userRole.includes('Inactive') &&
    userRole.some((role) => allowed.includes(role));

/** Whether the user may perform manager-level actions. */
export const isManagerOrAbove = (userRole) => hasAnyRole(userRole, MANAGER_OR_ABOVE);

/**
 * Whether the sidebar should show the assistant manager's own management menu,
 * which lists the Task Board alone. A manager already gets the full management
 * menu, so a user holding both roles would otherwise see PRICING and MANAGEMENT
 * twice, the second copy listing less than the first.
 * @param {Array<string>} userRole roles from the Auth0 token
 * @returns {boolean}
 */
export const showsAssistantManagerMenu = (userRole) =>
    !isManagerOrAbove(userRole) && hasAnyRole(userRole, ['Operation-Assistant Manager']);

/** Whether the user may perform assistant-manager-level actions. */
export const isAssistantManagerOrAbove = (userRole) => hasAnyRole(userRole, ASSISTANT_MANAGER_OR_ABOVE);

/**
 * Roles that may open Order Management while it is not released to users. The screen also
 * requires the ?ordermanagement=1 URL flag (features/shipments/lib/orderManagementAccess);
 * the flag is a screen-only extra gate. The websocket (OS-656) refuses il_packing_list
 * requests from anyone who is not an Administrator, so a typed URL alone gets no data.
 * At release, drop this gate, the URL flag and the server check together.
 */
export const ORDER_MANAGEMENT_ROLES = ['Administrator'];

/** Whether the user may see the unreleased Order Management view. */
export const canUseOrderManagement = (userRole) => hasAnyRole(userRole, ORDER_MANAGEMENT_ROLES);
